import { ChangeDetectionStrategy, Component, ElementRef, HostListener, inject, input, viewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { SessionService } from '../session.service';
import { LanguageSwitcherComponent } from './language-switcher.component';
import { APP_BRAND } from '../branding/environment.config';
import { AccountActionsService } from '../account-actions.service';
import { AuthDialogService } from '../auth-dialog.service';
import { MobileNavigationService } from '../mobile-navigation.service';
import { HeaderMenuService } from './header-menu.service';
import { BalanceVisibilityService } from '../balance-visibility.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterLink, TranslateModule, LanguageSwitcherComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderComponent {
  readonly compact = input(false);

  public session: SessionService = inject(SessionService);
  protected readonly balanceVisibility = inject(BalanceVisibilityService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly accountActions = inject(AccountActionsService);
  protected readonly authDialog = inject(AuthDialogService);
  protected readonly mobileNavigation = inject(MobileNavigationService);
  protected readonly headerMenu = inject(HeaderMenuService);
  protected readonly brand = APP_BRAND;
  private readonly profileTrigger = viewChild<ElementRef<HTMLButtonElement>>('profileTrigger');

  toggleProfileMenu(): void {
    this.headerMenu.toggleProfile();
  }

  closeProfileMenu(): void {
    this.headerMenu.closeProfile();
  }

  openChangePassword(): void {
    this.prepareProfileDialog();
    this.accountActions.openChangePassword();
  }

  openChangeUsername(): void {
    this.prepareProfileDialog();
    this.accountActions.openChangeUsername();
  }

  logout(): void {
    this.headerMenu.closeProfile();
    this.accountActions.logout();
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    if (!this.headerMenu.isProfileOpen()) {
      return;
    }

    const target = event.target as Node | null;
    const profile = this.host.nativeElement.querySelector('.app-header__profile');
    if (target && profile?.contains(target)) {
      return;
    }

    this.headerMenu.closeProfile();
  }

  @HostListener('document:keydown.escape', ['$event'])
  protected onEscape(event: Event): void {
    if (!this.headerMenu.isProfileOpen()) {
      return;
    }

    event.preventDefault();
    this.headerMenu.closeProfile();
    queueMicrotask(() => this.profileTrigger()?.nativeElement.focus({ preventScroll: true }));
  }

  private prepareProfileDialog(): void {
    this.profileTrigger()?.nativeElement.focus({ preventScroll: true });
    this.headerMenu.closeProfile();
  }
}
